<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          The rules under Section 16 of the Exchange Act require these
          "insiders" to report most of their transactions involving the
          company's equity securities to the
          <a href="https://www.sec.gov/" target="_blank">SEC</a>.
        </p>
      </div>
    </help-drawer>
    <v-form name="CompanyOfficial" class="form" data-test-id="company-official-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.COMPANY_OFFICIAL.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="company-official-header">
              <span class="headerlight">
                Are you a
                <strong>
                  member of the board of directors, 10% shareholder
                </strong>
                or <strong>policy-making officer</strong>
                of a publicly held company?
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isCompanyOfficialComplete && formHidden && !isSyncing"
          :submitted="isCompanyOfficialComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group
                v-model="companyOfficial"
                :error-messages="companyOfficialErrors"
                hide-details="auto"
                @change="$v.companyOfficial.$touch()"
                data-test-id="company-official-radio-group"
              >
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  label="Yes"
                  :value="true"
                  data-test-id="company-official-yes"
                ></v-radio>
                <div class="expander" v-show="companyOfficial">
                  <label for="companyName" data-test-id="company-name-label">Company Name</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="companyName"
                    v-model.trim="companyName"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.companyName.$touch()"
                    @blur="$v.companyName.$touch()"
                    :error-messages="companyNameErrors"
                    hide-details="auto"
                    data-test-id="company-name-input"
                  />
                  <label for="tickerSymbol" data-test-id="ticker-symbol-label">Ticker Symbol</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="5"
                    name="tickerSymbol"
                    v-model.trim="tickerSymbol"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.tickerSymbol.$touch()"
                    @blur="$v.tickerSymbol.$touch()"
                    :error-messages="tickerSymbolErrors"
                    hide-details="auto"
                    class="ticker"
                    data-test-id="ticker-symbol-input"
                  />
                </div>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio"
                  label="No"
                  :value="false"
                  data-test-id="company-official-no"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="company-official-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'CompanyOfficial',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    companyOfficial: null,
    companyName: null,
    tickerSymbol: null,
    MENUS: MENUS,
  }),

  validations() {
    return {
      companyOfficial: {
        required,
      },
      companyName: {
        required: requiredIf(() => this.companyOfficial),
      },
      tickerSymbol: {
        required: requiredIf(() => this.companyOfficial),
      },
      validationGroup: ['companyOfficial', 'companyName', 'tickerSymbol'],
    };
  },

  computed: {
    // Is company official complete
    isCompanyOfficialComplete() {
      return this.isPageComplete(MENUS.CLIENT.COMPANY_OFFICIAL.id);
    },
    
    /* Dynamic errors */
    companyOfficialErrors() {
      const errors = [];
      if (!this.$v.companyOfficial.$dirty) return errors;
      !this.$v.companyOfficial.required &&
        errors.push('A selection is required');
      return errors;
    },
    companyNameErrors() {
      const errors = [];
      if (!this.$v.companyName.$dirty) return errors;
      !this.$v.companyName.required && errors.push('Company Name is required');
      return errors;
    },
    tickerSymbolErrors() {
      const errors = [];
      if (!this.$v.tickerSymbol.$dirty) return errors;
      !this.$v.tickerSymbol.required &&
        errors.push('Ticker Symbol is required');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.COMPANY_OFFICIAL.id);
      return page && page.status === 'IN_PROGRESS';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = this.companyOfficial
        ? {
            companyName: this.companyName,
            tickerSymbol: this.tickerSymbol,
          }
        : {};

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isCompanyOfficialComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isCompanyOfficialComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.COMPANY_OFFICIAL.id);
    // Initialize form data for current interview
    this.formHidden = this.isCompanyOfficialComplete;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If company official is complete, hide the form
      if (this.isCompanyOfficialComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.expander {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--secondary-color);

  .ticker {
    ::v-deep .v-input__slot {
      max-width: 90px !important;
    }
  }
}

.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
